import CampoMoeda from '../../componentes/CampoMoeda'
import type { Divergencias, ProdutoComPreco } from '../../importacao/divergencias'

interface Props {
  divergencias: Divergencias
  nomeTabela: string
  inativar: Set<string>
  tirar: Set<string>
  precosEditados: Record<string, string>
  aoMudar: (d: { inativar: Set<string>; tirar: Set<string> }) => void
  aoEditarPreco: (codigo: string, valor: string) => void
  aoConfirmar: () => void
}

export default function TelaDivergencias({
  divergencias, nomeTabela, inativar, tirar, precosEditados, aoMudar, aoEditarPreco, aoConfirmar,
}: Props) {
  const { novos, sumidos, semPreco, paridade } = divergencias
  function decidirSumido(p: ProdutoComPreco, acao: 'tirar' | 'inativar') {
    const nInativar = new Set(inativar), nTirar = new Set(tirar)
    if (acao === 'inativar') { nInativar.add(p.produto_id); nTirar.delete(p.produto_id) }
    else { nTirar.add(p.produto_id); nInativar.delete(p.produto_id) }
    aoMudar({ inativar: nInativar, tirar: nTirar })
  }
  const todosSumidosDecididos = sumidos.every(
    (p) => inativar.has(p.produto_id) || tirar.has(p.produto_id),
  )
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 space-y-4">
      <h3 className="font-medium text-slate-800">Divergências — tabela {nomeTabela}</h3>

      {/*
        As duas conferências da caixa fechada. ⚠ É AVISO, NUNCA IMPEDIMENTO: o
        botão de aplicar não olha para `paridade`, porque ela decidiu em 28/08
        que os dois preços podem divergir de verdade (a fábrica cobra caro para
        quebrar caixa). A regra do projeto é "avisa, mas nunca impede de vender".
        Vem primeiro no bloco porque é a única que fala do arquivo INTEIRO — se
        as colunas estiverem trocadas, decidir sobre novos e sumidos é perda de
        tempo. O texto sai pronto de `conferirParidade`; a tela não reescreve.
      */}
      {paridade.length > 0 && (
        <ul className="space-y-1">
          {paridade.map((aviso, i) => (
            <li
              key={`${aviso.tipo}-${aviso.codigo ?? i}`}
              className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 break-words"
            >
              ⚠ {aviso.texto}
            </li>
          ))}
        </ul>
      )}

      {novos.length > 0 && (
        <p className="text-sm text-emerald-700 bg-emerald-50 rounded p-2">
          ✚ {novos.length} produto(s) novo(s) serão adicionados à tabela.
        </p>
      )}

      {sumidos.length > 0 && (
        <div>
          <p className="text-sm text-amber-700">
            ⚠ {sumidos.length} produto(s) estão na tabela e não vieram na planilha:
          </p>
          <div className="flex gap-2 my-2">
            <button className="text-xs underline text-teal-700"
              onClick={() => aoMudar({ inativar: new Set(), tirar: new Set(sumidos.map((p) => p.produto_id)) })}>
              Tirar todos da tabela
            </button>
            <button className="text-xs underline text-teal-700"
              onClick={() => aoMudar({ tirar: new Set(), inativar: new Set(sumidos.map((p) => p.produto_id)) })}>
              Inativar todos
            </button>
          </div>
          <ul className="space-y-1">
            {sumidos.map((p) => (
              <li key={p.produto_id} className="flex items-center justify-between text-sm">
                <span>{p.descricao} <span className="text-slate-400">({p.codigo})</span></span>
                <span className="flex gap-1">
                  <button
                    className={`text-xs rounded px-2 py-0.5 ${tirar.has(p.produto_id) ? 'bg-teal-700 text-white' : 'bg-slate-100'}`}
                    onClick={() => decidirSumido(p, 'tirar')}>tirar</button>
                  <button
                    className={`text-xs rounded px-2 py-0.5 ${inativar.has(p.produto_id) ? 'bg-red-600 text-white' : 'bg-slate-100'}`}
                    onClick={() => decidirSumido(p, 'inativar')}>inativar</button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {semPreco.length > 0 && (
        <div>
          <p className="text-sm text-amber-700">
            ⚠ {semPreco.length} produto(s) vieram sem preço — digite o valor ou deixe em branco (mantém o preço
            atual; entra sem preço se ainda não tiver):
          </p>
          <ul className="space-y-1 mt-1">
            {semPreco.map((p) => (
              <li key={p.codigo} className="flex items-center justify-between text-sm">
                <span>{p.descricao} <span className="text-slate-400">({p.codigo})</span></span>
                <CampoMoeda
                  aria-label={`Preço de ${p.codigo}`} placeholder="preço"
                  value={precosEditados[p.codigo] ?? ''}
                  onChange={(texto) => aoEditarPreco(p.codigo, texto)}
                  className="w-28 border border-slate-300 rounded px-2 py-0.5 text-right shrink-0"
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        onClick={aoConfirmar}
        disabled={!todosSumidosDecididos}
        className="w-full bg-teal-700 text-white rounded-lg py-2 font-medium disabled:opacity-50">
        Aplicar importação
      </button>
      {!todosSumidosDecididos && (
        <p className="text-xs text-amber-600">Decida o que fazer com cada produto que sumiu.</p>
      )}
    </div>
  )
}
